import React from 'react';
import styles from './Player.module.scss';
import {MediaSource, MediaSourceRef} from "./components/mediaSource/MediaSource";
import {Controller} from "./components/controller/Controller";
import {FullScreenPlayer} from "./components/fullScreen/FullScreenPlayer";
import {usePlayerSelector} from "../../../store/slices/player/playerSlice";
import {useCastSelector} from "../../../store/slices/cast/castSlice";
import {useBreakpoint} from "../../../hooks/useBreakpoint";
import {PlayerEventBus} from "./playerEventBus";
import {CastBridge} from "../../../native/cast/CastBridge";

const Player: React.FC = () => {
    const {nowPlaying, status} = usePlayerSelector(['nowPlaying', 'status'])
    const {state: castState, position: castPosition} = useCastSelector(['state', 'position']);
    const isCasting = castState === 'started';
    const mediaSourceRef = React.useRef<MediaSourceRef>(null);
    const [isReady, setIsReady] = React.useState<boolean>(false);
    const [isFullScreen, setIsFullScreen] = React.useState(false);

    const screen = useBreakpoint();
    const isResponsive = screen !== 'desktop';

    // Keyboard media keys (MediaTrackNext, MediaTrackPrevious, MediaPlayPause)
    // We intercept these at the document level because the YouTube iframe
    // owns the Media Session API and overrides any handlers we set.
    React.useEffect(() => {
        const handleMediaKey = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'MediaTrackNext':
                    e.preventDefault();
                    PlayerEventBus.emit('next');
                    break;
                case 'MediaTrackPrevious':
                    e.preventDefault();
                    PlayerEventBus.emit('prev');
                    break;
                case 'MediaPlayPause':
                    e.preventDefault();
                    if (status === 'Playing') {
                        PlayerEventBus.emit('pause');
                    } else {
                        PlayerEventBus.emit('play');
                    }
                    break;
                case 'MediaStop':
                    e.preventDefault();
                    PlayerEventBus.emit('pause');
                    break;
            }
        };

        document.addEventListener('keydown', handleMediaKey);
        return () => document.removeEventListener('keydown', handleMediaKey);
    }, [status]);

    // Media Session metadata + action handlers — for OS media overlay, lock screen, car display.
    // Handlers must be re-registered on every nowPlaying change because the YouTube iframe
    // reclaims the Media Session each time it loads a new video.
    React.useEffect(() => {
        if (!('mediaSession' in navigator) || !nowPlaying) return;

        navigator.mediaSession.metadata = new MediaMetadata({
            title: nowPlaying.song.name,
            artist: nowPlaying.song.artist,
            artwork: nowPlaying.song.img
                ? [{src: nowPlaying.song.img, sizes: '512x512', type: 'image/jpeg'}]
                : [],
        });

        navigator.mediaSession.setActionHandler('play', () => PlayerEventBus.emit('play'));
        navigator.mediaSession.setActionHandler('pause', () => PlayerEventBus.emit('pause'));
        navigator.mediaSession.setActionHandler('nexttrack', () => PlayerEventBus.emit('next'));
        navigator.mediaSession.setActionHandler('previoustrack', () => PlayerEventBus.emit('prev'));
    }, [nowPlaying]);

    // Keep the OS playback state in sync so the car/lock screen shows the right play/pause icon
    React.useEffect(() => {
        if (!('mediaSession' in navigator)) return;
        navigator.mediaSession.playbackState =
            status === 'Playing' ? 'playing'
            : status === 'Paused' ? 'paused'
            : 'none';
    }, [status]);

    // Allow playing playlist from local storage, and prevent the app from a crash
    React.useEffect(() => {
        if (status === 'Buffering') {
            setIsReady(true);
        }
    }, [status])

    // Handle browser 'Back' button to close the overlay
    React.useEffect(() => {
        const handlePopState = () => {
            setIsFullScreen(false);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Only trigger full screen on mobile and if not already full screen
    const handleContainerClick = () => {
        if (isResponsive && !isFullScreen) {
            setIsFullScreen(true);
            window.history.pushState({overlay: true}, '');
        }
    };

    const getPlayerCurrentTime = (): number => {
        if (isCasting) return castPosition ?? 0;
        return (mediaSourceRef.current?.player?.getCurrentTime() as number | undefined) ?? 0;
    }

    const handleBackClicked = () => {
        window.history.back();
    }

    return (
        nowPlaying ? (
                <div
                    className={`${styles.player__container} ${isReady ? '' : styles.player__init} ${isFullScreen ? styles.player__fullScreen : ''}`}
                    onClick={handleContainerClick}
                >
                    <CastBridge/>
                    <div className={`${styles.player__media} ${isFullScreen ? styles.player__mediaHidden : ''}`}>
                        <MediaSource
                            ref={mediaSourceRef}
                            isFullScreen={isFullScreen}
                            hideVisuals={isFullScreen}
                        />
                    </div>
                    {isFullScreen ? (
                        <FullScreenPlayer
                            onBack={handleBackClicked}
                            getPlayerCurrentTime={getPlayerCurrentTime}
                        />
                    ) : (
                        <>
                            <div className={styles.player__player}>
                                <Controller
                                    getPlayerCurrentTime={getPlayerCurrentTime}
                                    isFullScreen={isFullScreen}
                                />
                            </div>
                            <div className={styles.player__actions}/>
                        </>
                    )}
                </div>
            )
            : <></>
    );
};

export default Player;
